import React, { useEffect, useState, useMemo, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Megaphone, Plus, Copy, Trash2, Users, Link2, Download, X, Share2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const BASE = (typeof window !== "undefined" ? window.location.origin : "");

const KIND_OPTIONS = [
  { value: "", label: "Sin preselección" },
  { value: "community", label: "Comunidad (solo miembro)" },
  { value: "business", label: "Negocio" },
  { value: "venture", label: "Emprendimiento" },
  { value: "professional", label: "Profesional" },
  { value: "ngo", label: "ONG / Fundación" },
  { value: "church", label: "Iglesia" },
  { value: "community_leader", label: "Líder Comunitario" },
];

const buildUrl = (slug, kind) => {
  let url = `${BASE}/inscribirse?ref=${encodeURIComponent(slug)}`;
  if (kind) url += `&kind=${encodeURIComponent(kind)}`;
  return url;
};

const CampaignsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ slug: "", name: "", description: "", kind: "" });
  const [creating, setCreating] = useState(false);
  const [viewSlug, setViewSlug] = useState(null); // slug currently open (QR modal)
  const [users, setUsers] = useState({ items: [], total: 0 });
  const [usersLoading, setUsersLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/campaigns");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError("");
    try {
      await api.post("/admin/campaigns", form);
      setForm({ slug: "", name: "", description: "", kind: "" });
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`¿Eliminar la campaña "${slug}"? Los usuarios ya registrados se conservan pero quedan sin agrupación.`)) return;
    try {
      await api.delete(`/admin/campaigns/${slug}`);
      load();
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Enlace copiado al portapapeles.");
    } catch (err) {
      console.error("clipboard failed", err);
      window.prompt("Copia este enlace manualmente:", text);
    }
  };

  const openQrModal = async (slug) => {
    setViewSlug(slug);
    setUsersLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns/${slug}/users`);
      setUsers(data);
    } catch (err) {
      setUsers({ items: [], total: 0 });
    } finally {
      setUsersLoading(false);
    }
  };

  const closeModal = () => { setViewSlug(null); setUsers({ items: [], total: 0 }); };

  const downloadQr = () => {
    const canvas = document.getElementById(`qr-canvas-${viewSlug}`);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `qr-${viewSlug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const activeCampaign = useMemo(() => items.find((c) => c.slug === viewSlug), [items, viewSlug]);
  const activeUrl = viewSlug ? buildUrl(viewSlug, activeCampaign?.kind) : "";

  return (
    <div className="mt-6" data-testid="admin-campaigns-tab">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-11 h-11 rounded-full bg-orange/15 text-orange flex items-center justify-center">
          <Megaphone size={20} />
        </span>
        <div>
          <p className="eyebrow text-orange">Campañas</p>
          <h3 className="font-display text-2xl text-teal-deep leading-tight">Enlaces y QR para tus eventos</h3>
          <p className="text-xs text-teal-soft mt-1">Crea un enlace único por evento. El QR abre directamente el formulario de inscripción y cada persona que se registre con él queda vinculada a esa campaña para que puedas verlos aquí (ideal para rifas, invitaciones y métricas).</p>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="bg-cream/50 border border-gray-200 rounded-2xl p-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3 items-end" data-testid="campaigns-create-form">
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Slug (en el enlace)</label>
          <input
            required
            minLength={2}
            maxLength={60}
            placeholder="cafe-solidaridad"
            className="field-input"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            data-testid="campaigns-slug"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Nombre</label>
          <input
            required
            minLength={2}
            maxLength={120}
            placeholder="Café de la Solidaridad"
            className="field-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="campaigns-name"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Descripción (opcional)</label>
          <input
            maxLength={500}
            placeholder="Evento fin de año — rifa"
            className="field-input"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="campaigns-description"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-teal-soft font-bold mb-1">Tipo de perfil del QR</label>
          <select
            className="field-input"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="campaigns-kind"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value || "none"} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button type="submit" disabled={creating} className="btn-orange justify-center disabled:opacity-60 lg:col-span-4" data-testid="campaigns-create">
          <Plus size={14} /> {creating ? "Creando…" : "Crear campaña"}
        </button>
      </form>

      {error && <p className="text-red-600 text-sm mt-3" data-testid="campaigns-error">{error}</p>}

      {/* List */}
      <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="campaigns-list">
        {loading && items.length === 0 && (
          <p className="text-teal-soft text-sm">Cargando…</p>
        )}
        {!loading && items.length === 0 && (
          <div className="col-span-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
            <Megaphone className="mx-auto mb-3 text-teal-soft" size={28} />
            <p className="text-teal-soft text-sm">Aún no tienes campañas. Crea la primera con el formulario de arriba.</p>
          </div>
        )}
        {items.map((c) => (
          <div key={c.slug} className="bg-white border border-gray-200 rounded-2xl p-5" data-testid={`campaign-row-${c.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h4 className="font-display text-xl text-teal-deep leading-tight">{c.name}</h4>
                <p className="text-xs text-teal-soft mt-1 break-all">{buildUrl(c.slug, c.kind)}</p>
                {c.kind && (
                  <span className="inline-block mt-1.5 text-[10px] font-bold uppercase tracking-wider bg-teal-deep/10 text-teal-deep rounded-full px-2 py-0.5" data-testid={`campaign-kind-${c.slug}`}>
                    {(KIND_OPTIONS.find((o) => o.value === c.kind) || {}).label || c.kind}
                  </span>
                )}
                {c.description && <p className="text-sm text-teal-deep mt-2">{c.description}</p>}
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 bg-orange/10 text-orange text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1">
                <Users size={11} /> {c.user_count}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openQrModal(c.slug)}
                className="btn-orange text-xs px-3 py-2"
                data-testid={`campaign-view-${c.slug}`}
              >
                <Share2 size={12} /> Ver QR e inscritos
              </button>
              <button
                type="button"
                onClick={() => copy(buildUrl(c.slug, c.kind))}
                className="btn-teal-outline text-xs px-3 py-2"
                data-testid={`campaign-copy-${c.slug}`}
              >
                <Link2 size={12} /> Copiar enlace
              </button>
              <button
                type="button"
                onClick={() => remove(c.slug)}
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-full px-3 py-2 transition-colors"
                data-testid={`campaign-delete-${c.slug}`}
              >
                <Trash2 size={12} /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* QR Modal */}
      {viewSlug && activeCampaign && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-6" onClick={closeModal} data-testid="campaign-modal">
          <div className="bg-white w-full md:max-w-2xl rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-6 border-b border-gray-100">
              <button type="button" onClick={closeModal} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center">
                <X size={16} />
              </button>
              <p className="eyebrow text-orange">Campaña</p>
              <h3 className="font-display text-2xl text-teal-deep leading-tight">{activeCampaign.name}</h3>
            </div>

            <div className="p-6 grid md:grid-cols-2 gap-6">
              {/* QR + URL */}
              <div className="text-center">
                <div className="bg-white border border-gray-200 rounded-2xl p-4 inline-block">
                  <QRCodeCanvas
                    id={`qr-canvas-${viewSlug}`}
                    value={activeUrl}
                    size={200}
                    bgColor="#ffffff"
                    fgColor="#1F4E47"
                    level="M"
                    includeMargin={false}
                    data-testid="campaign-qr"
                  />
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  <button type="button" onClick={downloadQr} className="btn-orange justify-center" data-testid="campaign-download-qr">
                    <Download size={14} /> Descargar QR
                  </button>
                  <button type="button" onClick={() => copy(activeUrl)} className="btn-teal-outline justify-center">
                    <Copy size={14} /> Copiar enlace
                  </button>
                </div>
                <p className="text-xs text-teal-soft mt-3 break-all">{activeUrl}</p>
              </div>

              {/* Users */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-display text-lg text-teal-deep">Inscritos</h4>
                  <span className="text-xs font-bold uppercase tracking-wider bg-teal/10 text-teal rounded-full px-3 py-1">
                    {users.total}
                  </span>
                </div>
                {usersLoading ? (
                  <p className="text-teal-soft text-sm">Cargando…</p>
                ) : users.items.length === 0 ? (
                  <p className="text-teal-soft text-sm text-center py-6">
                    Nadie se ha registrado todavía con este enlace. Comparte el QR en tu evento.
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-80 overflow-y-auto" data-testid="campaign-users-list">
                    {users.items.map((u) => (
                      <li key={u.id} className="bg-cream/60 rounded-xl p-3 text-sm" data-testid={`campaign-user-${u.id}`}>
                        <div className="font-bold text-teal-deep truncate">{u.full_name || "—"}</div>
                        <div className="text-xs text-teal-soft truncate">
                          {u.email || u.phone || "—"} · {(u.created_at || "").slice(0, 10)}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignsTab;
